/**
 * SuggestedChips — Unified suggestion chips (ChatGPT/Gemini pattern)
 *
 * Renders suggested tasks first (instant, from studio config), then
 * questions (async, LLM-generated) as flat, borderless pill chips.
 *
 * Design decisions (from UX audit):
 * - No container box/border — chips float freely below the AI response
 * - No section headers — self-explanatory affordance
 * - text-sm (not text-xs) for readability on financial question text
 * - Outlined pills: border only, background on hover
 * - Tasks differentiated by ✨ icon prefix
 * - Tasks render first: they're available instantly (static config),
 *   while questions require an LLM call and arrive later
 * - 3-4 chips max visible to avoid cognitive overload (Hick's Law)
 */

import React from 'react';
import { Sparkles, ArrowUpRight } from 'lucide-react';
import type { SuggestedStudioTask } from './chat/hooks/useSuggestedContent';

interface SuggestedChipsProps {
  questions?: string[];
  tasks?: SuggestedStudioTask[];
  onQuestionClick?: (question: string) => void;
  onTaskClick?: (task: SuggestedStudioTask) => void;
  isLoadingQuestions?: boolean;
  isLoadingTasks?: boolean;
  className?: string;
}

const SuggestedChips: React.FC<SuggestedChipsProps> = ({
  questions = [],
  tasks = [],
  onQuestionClick,
  onTaskClick,
  isLoadingQuestions = false,
  isLoadingTasks = false,
  className = '',
}) => {
  const hasContent =
    questions.length > 0 || tasks.length > 0 || isLoadingQuestions || isLoadingTasks;

  if (!hasContent) return null;

  return (
    <div className={`flex flex-wrap gap-2 mt-3 animate-fade-in ${className}`}>
      {/* Task chips first — available instantly (static config) */}
      {tasks.map((task) => (
        <button
          key={`t-${task.id}`}
          onClick={() => onTaskClick?.(task)}
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm rounded-full
                     border border-nb-border text-nb-text-secondary bg-transparent
                     hover:bg-nb-surface-hover hover:text-nb-text hover:border-nb-accent/40
                     focus:outline-none focus:ring-2 focus:ring-nb-accent/30
                     active:scale-[0.97] transition-all cursor-pointer
                     leading-snug min-h-[44px]"
          aria-label={`Tarea: ${task.name}`}
        >
          <Sparkles className="w-3 h-3 flex-shrink-0" />
          {task.name}
        </button>
      ))}

      {/* Task loading placeholders */}
      {isLoadingTasks &&
        tasks.length === 0 &&
        Array.from({ length: 2 }).map((_, i) => (
          <div
            key={`task-skeleton-${i}`}
            className="h-8 rounded-full border border-nb-border bg-nb-surface animate-pulse"
            style={{ width: `${110 + i * 20}px` }}
          />
        ))}

      {/* Question chips — async, LLM-generated (arrive later) */}
      {questions.map((question, index) => (
        <button
          key={`q-${index}`}
          onClick={() => onQuestionClick?.(question)}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] rounded-full
                     border border-nb-border text-nb-text-secondary
                     bg-nb-surface hover:bg-nb-surface-hover hover:text-nb-text hover:border-nb-accent/40
                     shadow-sm hover:shadow
                     focus:outline-none focus:ring-2 focus:ring-nb-accent/30
                     active:scale-[0.97] transition-all cursor-pointer
                     leading-snug min-h-[44px]"
          aria-label={`Pregunta sugerida: ${question}`}
        >
          <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
          {question}
        </button>
      ))}

      {/* Question loading placeholders */}
      {isLoadingQuestions &&
        questions.length === 0 &&
        Array.from({ length: 3 }).map((_, i) => (
          <div
            key={`skeleton-${i}`}
            className="h-8 rounded-full border border-nb-border bg-nb-surface animate-pulse"
            style={{ width: `${100 + i * 30}px` }}
          />
        ))}
    </div>
  );
};

export default SuggestedChips;
